<script setup>
import { ref, computed } from 'vue'
import { Head, Link } from '@inertiajs/vue3'
import { useFilterTransition } from '@/composables/useFilterTransition'
import { ChevronLeft, ChevronRight, LayoutGrid, Rows3, X, SlidersHorizontal } from 'lucide-vue-next'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageHero from '@/Components/Marketing/PageHero.vue'
import FilterPopover from '@/Components/Tutors/FilterPopover.vue'
import SortMenu from '@/Components/Tutors/SortMenu.vue'
import TutorCard from '@/Components/TutorCard.vue'
import { Button } from '@/Components/ui/button'
import { becomeATutor } from '@/routes/index'

/**
 * The tutor listing.
 *
 * Two things changed from the previous version. The four "video lessons / group
 * sessions / 1:1 tutors / study materials" tiles that opened the page are gone:
 * they counted things rather than helping anyone choose, and group sessions are
 * not sold at all. And the single "Filters" dropdown that hid subject, level and
 * exam board behind three clicks is now one control per dimension, each showing
 * what it has applied.
 */
const props = defineProps({
  tutors: { type: Object, required: true },
  subjects: { type: Array, default: () => [] },
  levels: { type: Array, default: () => [] },
  examboards: { type: Array, default: () => [] },
  filters: { type: Object, default: () => ({}) },
})

const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : [])

const activeSubjects = ref(
  Array.from(new Set([...toArray(props.filters.subjects), ...toArray(props.filters.subject)]))
)
const activeLevels = ref(toArray(props.filters.levels))
const activeExamboards = ref(toArray(props.filters.examboards))
const sortBy = ref(props.filters.sort || 'popular')
const viewMode = ref('grid')

/*
 * There is no "Offers" filter any more.
 *
 * It held three options. Group tutoring had already gone, being off sale while
 * Bindle Live is built. Video content and study material came from a box a tutor
 * ticked at sign-up rather than from anything they had published, so filtering by
 * them returned five tutors with empty libraries. That left one-to-one, which
 * every listed tutor offers — a filter that cannot narrow anything is a control
 * that wastes a click and a row of the toolbar.
 *
 * A tutor arriving on an old link with `teaching_methods` in it still gets a
 * filtered list; the server side is untouched.
 */

const sortOptions = [
  { value: 'popular', label: 'Most students' },
  { value: 'price_low', label: 'Price: low to high' },
  { value: 'price_high', label: 'Price: high to low' },
  { value: 'newest', label: 'Recently joined' },
]

const buildParams = (extra = {}) => {
  const params = {}
  if (activeSubjects.value.length) params.subjects = activeSubjects.value
  if (activeLevels.value.length) params.levels = activeLevels.value
  if (activeExamboards.value.length) params.examboards = activeExamboards.value
  if (sortBy.value && sortBy.value !== 'popular') params.sort = sortBy.value
  return { ...params, ...extra }
}

const { pending, visit } = useFilterTransition('/tutors')

const apply = (extra = {}, preserveScroll = true) => visit(buildParams(extra), { preserveScroll })

/** Every applied filter as a removable chip, so nothing is hidden in a panel. */
const activeChips = computed(() => {
  const nameOf = (list, slug, key = 'slug') => list.find((item) => item[key] === slug)?.name ?? slug

  return [
    ...activeSubjects.value.map((slug) => ({ group: 'subjects', slug, label: nameOf(props.subjects, slug) })),
    ...activeLevels.value.map((slug) => ({ group: 'levels', slug, label: nameOf(props.levels, slug) })),
    ...activeExamboards.value.map((slug) => ({ group: 'examboards', slug, label: nameOf(props.examboards, slug) })),
  ]
})

const removeChip = (chip) => {
  const target = {
    subjects: activeSubjects,
    levels: activeLevels,
    examboards: activeExamboards,
  }[chip.group]

  target.value = target.value.filter((slug) => slug !== chip.slug)
  apply()
}

const clearAll = () => {
  activeSubjects.value = []
  activeLevels.value = []
  activeExamboards.value = []
  apply()
}

const changePage = (page) => apply({ page }, false)

const pageNumbers = computed(() => {
  const { current_page: current, last_page: last } = props.tutors
  return Array.from({ length: last }, (_, i) => i + 1).filter(
    (page) => page === 1 || page === last || (page >= current - 1 && page <= current + 1)
  )
})
</script>

<template>
  <Head>
    <title>Our tutors — Bindle</title>
    <meta name="description" content="Browse Bindle tutors by subject, level and exam board. Every profile shows where they studied and what they charge." />
  </Head>

  <AppLayout>
    <PageHero
      eyebrow="Our tutors"
      title="Every tutor, and what they have actually achieved."
      lead="Filter by subject, level or exam board. Each profile shows the university, the degree and the grades behind it, along with the weekly price."
    />

    <section class="py-10 md:py-14">
      <div class="container-page">
        <!-- ==================== FILTER BAR ==================== -->
        <div class="rounded-xl border border-line bg-surface p-4">
          <div class="flex flex-wrap items-center gap-3">
            <span class="hidden items-center gap-2 text-sm font-semibold text-muted sm:flex">
              <SlidersHorizontal class="size-4" /> Filter
            </span>

            <FilterPopover v-model="activeSubjects" label="Subject" :options="subjects" @change="apply()" />
            <FilterPopover v-model="activeLevels" label="Level" :options="levels" @change="apply()" />
            <FilterPopover v-model="activeExamboards" label="Exam board" :options="examboards" @change="apply()" />

            <!-- `ml-auto` only from sm: on a phone the filters wrap and the sort
                 control was pushed to the right edge on a line of its own,
                 orphaned from the controls it belongs with. -->
            <div class="flex w-full items-center gap-3 sm:ml-auto sm:w-auto">
              <SortMenu v-model="sortBy" :options="sortOptions" @change="apply()" />

              <div class="hidden items-center rounded-md border border-line p-0.5 sm:flex">
                <button
                  type="button"
                  class="grid size-9 place-items-center rounded-sm transition-colors"
                  :class="viewMode === 'grid' ? 'bg-brand text-brand-contrast' : 'text-muted hover:bg-surface-2'"
                  aria-label="Grid view"
                  :aria-pressed="viewMode === 'grid'"
                  @click="viewMode = 'grid'"
                >
                  <LayoutGrid class="size-4.5" />
                </button>
                <button
                  type="button"
                  class="grid size-9 place-items-center rounded-sm transition-colors"
                  :class="viewMode === 'list' ? 'bg-brand text-brand-contrast' : 'text-muted hover:bg-surface-2'"
                  aria-label="List view"
                  :aria-pressed="viewMode === 'list'"
                  @click="viewMode = 'list'"
                >
                  <Rows3 class="size-4.5" />
                </button>
              </div>
            </div>
          </div>

          <!-- Applied filters -->
          <div v-if="activeChips.length" class="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-4">
            <button
              v-for="chip in activeChips"
              :key="`${chip.group}-${chip.slug}`"
              type="button"
              class="inline-flex items-center gap-1.5 rounded-full border border-line-brand bg-brand-soft px-3 py-1.5 text-xs font-semibold text-brand-soft-text transition-colors hover:bg-brand hover:text-brand-contrast"
              @click="removeChip(chip)"
            >
              {{ chip.label }}
              <X class="size-3.5" />
            </button>
            <button
              type="button"
              class="ml-1 text-xs font-semibold text-muted underline-offset-4 hover:text-ink hover:underline"
              @click="clearAll"
            >
              Clear all
            </button>
          </div>
        </div>

        <!-- ==================== RESULTS ==================== -->
        <p class="mt-6 text-sm text-muted">
          <template v-if="tutors.total">
            Showing <strong class="text-ink">{{ tutors.from }}–{{ tutors.to }}</strong> of
            <strong class="text-ink">{{ tutors.total }}</strong>
            {{ tutors.total === 1 ? 'tutor' : 'tutors' }}
          </template>
        </p>

        <!--
          The grid crossfades while the server answers rather than being
          replaced and re-staggered. Each card keeps its own `:key`, so Vue
          moves the ones that survive a filter change instead of tearing the
          list down — which is what made it flash even when nothing changed.
        -->
        <div
          v-if="tutors.data.length"
          :class="[
            'mt-5 grid gap-5 transition-[opacity,transform] duration-200 ease-brand',
            pending ? 'translate-y-1 opacity-40' : 'translate-y-0 opacity-100',
            viewMode === 'grid'
              ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
              : 'grid-cols-1 xl:grid-cols-2',
          ]"
          aria-live="polite"
          :aria-busy="pending"
        >
          <TutorCard
            v-for="tutor in tutors.data"
            :key="tutor.id"
            :tutor="tutor"
            :view-mode="viewMode"
          />
        </div>

        <!-- Empty -->
        <div v-else class="mt-6 rounded-xl border border-dashed border-line-strong p-12 text-center">
          <h2 class="text-xl">No tutor matches those filters</h2>
          <p class="mx-auto mt-2 max-w-md text-[15px] text-muted">
            Try removing a filter — exam board and level together narrow things quickly.
          </p>
          <div class="mt-6 flex flex-wrap justify-center gap-3">
            <Button variant="secondary" @click="clearAll">Clear filters</Button>
            <Link :href="becomeATutor()"><Button variant="quiet">Teach this subject</Button></Link>
          </div>
        </div>

        <!-- ==================== PAGINATION ==================== -->
        <nav v-if="tutors.last_page > 1" class="mt-10 flex flex-wrap items-center justify-center gap-2" aria-label="Pagination">
          <Button
            variant="quiet"
            size="icon-sm"
            :disabled="tutors.current_page === 1"
            aria-label="Previous page"
            @click="changePage(tutors.current_page - 1)"
          >
            <ChevronLeft class="size-4" />
          </Button>

          <template v-for="(page, index) in pageNumbers" :key="page">
            <span v-if="index > 0 && page - pageNumbers[index - 1] > 1" class="px-1 text-muted">…</span>
            <Button
              :variant="page === tutors.current_page ? 'default' : 'quiet'"
              size="sm"
              class="min-w-10"
              :aria-current="page === tutors.current_page ? 'page' : undefined"
              @click="changePage(page)"
            >
              {{ page }}
            </Button>
          </template>

          <Button
            variant="quiet"
            size="icon-sm"
            :disabled="tutors.current_page === tutors.last_page"
            aria-label="Next page"
            @click="changePage(tutors.current_page + 1)"
          >
            <ChevronRight class="size-4" />
          </Button>
        </nav>
      </div>
    </section>
  </AppLayout>
</template>
